import { useState } from 'react'

import { OperationCountsSummary, OperationProgressBar, OperationStateBadge } from '@/components/operations/OperationProgress'
import { announceOperation, useOperations } from '@/hooks/useOperations'
import { ApiResponseError } from '@/lib/api-fetch'
import { cancelOperation } from '@/lib/api/client/operations'
import type { Operation, OperationItem, OperationListScope } from '@/lib/api/schemas/operations.schema'
import {
  ITEM_OUTCOME_LABELS,
  ITEM_OUTCOME_TONES,
  coverageLine,
  coverageReasons,
  formatOperationTime,
  itemOutcome,
  itemReasonText,
  marketplaceIdLabel,
  operationKindLabel,
  operationTitle,
} from '@/lib/operations/labels'

const SCOPES: readonly { value: OperationListScope; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'active', label: 'Активные' },
]

function cancelErrorText(cause: unknown): string {
  if (cause instanceof ApiResponseError && cause.status === 409) return 'Операция уже завершена, отменять нечего.'
  return cause instanceof Error ? cause.message : 'Не удалось отменить операцию.'
}

function OperationItemLine({ item }: { item: OperationItem }) {
  const outcome = itemOutcome(item)
  const reason = itemReasonText(item)
  const nextCheck = formatOperationTime(item.nextAttemptAt)
  const ids = Object.entries(item.marketplaceIds)
  return (
    <li data-item-id={item.id} data-item-outcome={outcome} className="space-y-1.5 px-4 py-3">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
        <span className="w-8 shrink-0 text-[12px] tab-nums text-ink-faint">{item.position}</span>
        <span className="min-w-0 flex-1 text-[13px] font-medium leading-5 text-ink">{item.label}</span>
        <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${ITEM_OUTCOME_TONES[outcome]}`}>{ITEM_OUTCOME_LABELS[outcome]}</span>
      </div>
      <div className="space-y-1 pl-11">
        {reason !== null ? (
          <p className={`text-[12px] leading-5 ${item.state === 'failed' ? 'text-st-unpaid' : 'text-ink-2'}`}>
            Причина: {reason}
          </p>
        ) : null}
        {item.warnings.length > 0 ? (
          <ul aria-label="Замечания" className="space-y-0.5 text-[12px] leading-5 text-gold-deep">
            {item.warnings.map((warning, index) => <li key={`${warning.code}:${index}`} data-warning-code={warning.code}>Замечание: {warning.message}</li>)}
          </ul>
        ) : null}
        {ids.length > 0 ? (
          <dl className="flex flex-wrap gap-x-4 gap-y-0.5 text-[12px] text-ink-muted">
            {ids.map(([key, value]) => (
              <div key={key} className="flex gap-1">
                <dt>{marketplaceIdLabel(key)}:</dt>
                <dd className="font-medium tab-nums text-ink-2">{value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {item.state === 'pending' && item.waitingReason !== null ? (
          <p data-item-waiting className="text-[12px] leading-5 text-ink-2">
            {item.waitingReason}
            {nextCheck !== null ? <span className="ml-1 text-ink-muted">Следующая проверка: {nextCheck}.</span> : null}
          </p>
        ) : null}
        {item.attempts > 1 ? <p className="text-[11px] text-ink-faint">Попыток: {item.attempts}</p> : null}
      </div>
    </li>
  )
}

function CancelControl({ operation }: { operation: Operation }) {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const confirm = async () => {
    setBusy(true)
    setError(null)
    try {
      announceOperation(await cancelOperation(operation.id))
      setConfirming(false)
    } catch (cause: unknown) {
      setError(cancelErrorText(cause))
    } finally {
      setBusy(false)
    }
  }

  if (!operation.cancellable && error === null) return null
  return (
    <div className="space-y-2">
      {confirming ? (
        <div role="group" aria-label="Подтверждение отмены" className="rounded-lg border border-st-unpaid/30 bg-st-unpaid/5 p-3 text-[13px] text-ink-2">
          <p>Отменить ожидающие позиции? Позиция, которая уже выполняется, завершится.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => { void confirm() }}>{busy ? 'Отменяем…' : 'Да, отменить'}</button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setConfirming(false)}>Нет</button>
          </div>
        </div>
      ) : operation.cancellable ? (
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setError(null); setConfirming(true) }}>Отменить операцию</button>
      ) : null}
      {error !== null ? <p role="alert" className="text-[12px] text-st-unpaid">{error}</p> : null}
    </div>
  )
}

function CoverageLine({ operation }: { operation: Operation }) {
  const coverage = operation.coverage ?? []
  const line = coverageLine(coverage)
  if (line === null) return null
  const reasons = coverageReasons(coverage)
  return (
    <p data-operation-coverage className="text-[12px] leading-5 text-ink-2" title={reasons || undefined}>
      Покрытие: {line}
    </p>
  )
}

function OperationRow({
  operation,
  expanded,
  items,
  onToggle,
}: {
  operation: Operation
  expanded: boolean
  items: OperationItem[] | null
  onToggle: () => void
}) {
  const created = formatOperationTime(operation.createdAt)
  const finished = formatOperationTime(operation.finishedAt)
  const panelId = `operation-items-${operation.id}`
  return (
    <li data-operation-id={operation.id} className="overflow-hidden rounded-xl border border-rule bg-paper">
      <div className="space-y-2.5 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold leading-5 text-ink">{operationTitle(operation)}</h2>
            <p className="mt-0.5 text-[12px] text-ink-muted">
              {operationKindLabel(operation.kind)}
              {created !== null ? ` · создана ${created}` : ''}
              {finished !== null ? ` · завершена ${finished}` : ''}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {operation.dryRun ? <span className="rounded-full border border-rule px-2 py-0.5 text-[11px] font-semibold text-ink-muted">Пробный запуск</span> : null}
            <OperationStateBadge state={operation.state} />
          </div>
        </div>
        <OperationProgressBar operation={operation} />
        <OperationCountsSummary counts={operation.counts} />
        <CoverageLine operation={operation} />
        <div className="flex flex-wrap items-start gap-2">
          <button type="button" className="btn btn-ghost btn-sm" aria-expanded={expanded} aria-controls={panelId} onClick={onToggle}>
            {expanded ? 'Скрыть позиции' : `Показать позиции (${operation.counts.total})`}
          </button>
          <CancelControl operation={operation} />
        </div>
      </div>
      {expanded ? (
        <div id={panelId} className="border-t border-rule bg-ivory/50">
          {items === null ? (
            <p role="status" className="px-4 py-3 text-[13px] text-ink-muted">Загружаем позиции…</p>
          ) : items.length === 0 ? (
            <p className="px-4 py-3 text-[13px] text-ink-muted">Позиций нет.</p>
          ) : (
            <ol aria-label="Позиции операции" className="divide-y divide-rule">
              {items.map((item) => <OperationItemLine key={item.id} item={item} />)}
            </ol>
          )}
        </div>
      ) : null}
    </li>
  )
}

export default function OperationsPage() {
  const [scope, setScope] = useState<OperationListScope>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const { operations, items, error, loaded, hasMore } = useOperations({ scope, expandedId })

  return (
    <div className="h-full overflow-y-auto bg-ivory">
      <div className="mx-auto max-w-4xl px-4 py-5 pb-[calc(96px+env(safe-area-inset-bottom))] sm:px-6 sm:py-8 md:px-10 md:py-10">
        <header className="mb-6 md:mb-8">
          <h1 className="truncate text-[26px] font-semibold leading-none tracking-tight text-ink md:text-[32px]">Операции</h1>
          <p className="mt-2 text-[13px] text-ink-muted">Очередь фоновых операций: заливка карточек на маркетплейсы и её результат по каждому изделию.</p>
          <div className="mt-5 h-px bg-rule" />
        </header>
        <div role="group" aria-label="Фильтр операций" className="mb-4 flex gap-2">
          {SCOPES.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={scope === option.value}
              onClick={() => setScope(option.value)}
              className={`h-9 rounded-full border px-4 text-[13px] font-medium transition-colors ${scope === option.value ? 'border-onyx bg-onyx text-ivory' : 'border-rule bg-paper text-ink-2 hover:border-rule-strong'}`}
            >
              {option.label}
            </button>
          ))}
        </div>
        {error !== null ? <p role="alert" className="mb-4 rounded-md border border-st-unpaid/30 bg-st-unpaid/5 p-3 text-sm text-st-unpaid">{error}</p> : null}
        {!loaded ? (
          <p aria-live="polite" className="rounded-lg border border-rule bg-paper p-5 text-sm text-ink-muted">Загружаем операции…</p>
        ) : operations.length === 0 ? (
          <p className="rounded-lg border border-rule bg-paper p-5 text-sm text-ink-muted">{scope === 'active' ? 'Сейчас ничего не выполняется.' : 'Операций пока не было.'}</p>
        ) : (
          <ul className="space-y-3">
            {operations.map((operation) => (
              <OperationRow
                key={operation.id}
                operation={operation}
                expanded={expandedId === operation.id}
                items={expandedId === operation.id && items?.operationId === operation.id ? items.items : null}
                onToggle={() => setExpandedId((current) => (current === operation.id ? null : operation.id))}
              />
            ))}
          </ul>
        )}
        {hasMore ? <p className="mt-4 text-center text-[12px] text-ink-muted">Показаны последние {operations.length} операций.</p> : null}
      </div>
    </div>
  )
}
